import {type ReactNode} from 'react';
import {DollarSign, Video} from 'lucide-react';
import {Badge, Card, CardContent, CardFooter} from '@kroll-resolver-crisp/ui-components-react';
import {type ChannelStatus} from '@/api/channels';
import {
  BoolValue,
  DescriptionDisclosure,
  DetailField,
  MetaGrid,
  MoreDetails,
  PlatformStatus,
  PriorSubmissions,
  TagList,
  formatDate
} from '@/components/channels/StatusCardParts';
import {formatCompactNumber} from '@/lib/itemMetadata';
import {cn} from '@/lib/utils';
import {SensitiveMedia} from '@/components/shared/SensitiveMedia';

export function ChannelStatusCard({
  channel,
  borderClassName,
  priorSubmissionCount,
  footer
}: {
  readonly channel: ChannelStatus;
  // The "already submitted" emphasis the page computes from the entity's prior submissions.
  readonly borderClassName?: string;
  readonly priorSubmissionCount?: number;
  // The entity's own actions (flag, classify, or the submitted state), rendered in the card footer.
  readonly footer?: ReactNode;
}) {
  const subscriberCount = formatCompactNumber(channel.subscriber_count);
  const videoCount = formatCompactNumber(channel.video_count);
  const viewCount = formatCompactNumber(channel.view_count);
  const topicCategories = channel.topic_categories ?? [];
  const keywords = (channel.keywords ?? '')
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean);
  const metaLine = [
    channel.custom_url,
    channel.published_at ? `Created ${formatDate(channel.published_at)}` : null,
    channel.hidden_subscriber_count
      ? 'Subscribers hidden'
      : subscriberCount
        ? `${subscriberCount} subscribers`
        : null,
    videoCount ? `${videoCount} videos` : null,
    viewCount ? `${viewCount} views` : null
  ].filter(Boolean);

  return (
    <Card className={cn('border', borderClassName ?? 'border-border')}>
      <CardContent className="flex flex-col gap-4">
        <div className="flex gap-4">
          <div className="group bg-card relative aspect-square w-50 shrink-0 overflow-hidden">
            {channel.thumbnail_url ? (
              <SensitiveMedia
                src={channel.thumbnail_url}
                alt={channel.title ?? channel.id}
                className="size-full object-cover"
              />
            ) : (
              <span className="text-muted-foreground flex size-full items-center justify-center">
                <Video className="size-8" />
              </span>
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-foreground truncate text-sm font-medium">
                  {channel.title ?? 'Untitled channel'}
                </p>
                {metaLine.length > 0 ? (
                  <p className="text-muted-foreground truncate text-xs">{metaLine.join(' · ')}</p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Badge variant="outline">
                  <Video />
                  Actor
                </Badge>
                {channel.is_channel_monetization_enabled ? (
                  <Badge variant="outline">
                    <DollarSign />
                    Monetised
                  </Badge>
                ) : null}
              </div>
            </div>
            {channel.description ? (
              <DescriptionDisclosure description={channel.description} />
            ) : null}
          </div>
        </div>

        <MetaGrid className="border-border border-t pt-4">
          <DetailField label="External ID">
            <span className="font-mono">{channel.id}</span>
          </DetailField>
          <DetailField label="Platform status">
            <PlatformStatus status={channel.privacy_status} />
          </DetailField>
          <DetailField label="Country">{channel.country ?? '—'}</DetailField>
          <DetailField label="Handle">{channel.custom_url ?? '—'}</DetailField>
          <DetailField label="Created">{formatDate(channel.published_at)}</DetailField>
          <DetailField label="Monetised">
            <BoolValue value={channel.is_channel_monetization_enabled} />
          </DetailField>
          <DetailField label="Made for Kids">
            <BoolValue value={channel.made_for_kids} />
          </DetailField>
          <DetailField label="Linked">
            <BoolValue value={channel.is_linked} />
          </DetailField>
          {priorSubmissionCount === undefined ? null : (
            <DetailField label="Prior submissions">
              <PriorSubmissions count={priorSubmissionCount} />
            </DetailField>
          )}
        </MetaGrid>

        {topicCategories.length > 0 || keywords.length > 0 ? (
          <MoreDetails className="border-border border-t pt-3">
            <MetaGrid className="border-border rounded-lg border p-3">
              {topicCategories.length > 0 ? (
                <DetailField label="Topics" className="col-span-full">
                  <TagList items={topicCategories} />
                </DetailField>
              ) : null}
              {keywords.length > 0 ? (
                <DetailField label="Keywords" className="col-span-full">
                  <TagList items={keywords} />
                </DetailField>
              ) : null}
            </MetaGrid>
          </MoreDetails>
        ) : null}
      </CardContent>
      {footer ? <CardFooter className="justify-end gap-2">{footer}</CardFooter> : null}
    </Card>
  );
}
